import { useCallback, useEffect, useState } from 'react';
import {
  createCityRequest,
  listCitiesByCountryRequest,
  listCountriesRequest
} from '@services/locations/locationsApi';

// Country/city data hook used by tour and location selection flows.
function useLocations() {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  const [submittingCity, setSubmittingCity] = useState(false);
  const [error, setError] = useState('');

  const fetchCountries = useCallback(async () => {
    // Loads countries once and can be reused for manual refresh if needed.
    setError('');
    setLoadingCountries(true);
    try {
      const response = await listCountriesRequest();
      setCountries(response.body?.countries || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingCountries(false);
    }
  }, []);

  const fetchCitiesByCountry = useCallback(async (countryId) => {
    // Country-scoped fetch because cities are dependent options.
    if (!countryId) {
      setCities([]);
      return;
    }
    setError('');
    setLoadingCities(true);
    try {
      const response = await listCitiesByCountryRequest(countryId);
      setCities(response.body?.cities || []);
    } catch (err) {
      setError(err.message);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  }, []);

  const createCity = useCallback(
    async (payload) => {
      // After creating a city, refresh current country cities to keep selector in sync.
      setError('');
      setSubmittingCity(true);
      try {
        await createCityRequest(payload);
        await fetchCitiesByCountry(payload.countryId);
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setSubmittingCity(false);
      }
    },
    [fetchCitiesByCountry]
  );

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  return {
    countries,
    cities,
    loadingCountries,
    loadingCities,
    submittingCity,
    error,
    fetchCitiesByCountry,
    createCity
  };
}

export default useLocations;
